import Accordion from '@platform-ui-common/components/Accordion/index.js';
import Button from '@platform-ui-common/components/Button/index.js';
import FormContainer from '@platform-ui-common/components/FormContainer/index.js';
import Markdown from '@platform-ui-common/components/Markdown/index.js';
import Typography from '@platform-ui-common/components/Typography/index.js';
import Color from '@platform-ui-common/utilities/Color.js';
import { useCreateApplication } from '@platform-ui/application/hooks/index.js';
import useApplicationConfig from '@platform-ui/application/hooks/useApplicationConfig.js';
import ApplicantRoutes from '@platform-ui/application/utils/ApplicantRoutes.js';
import useAuth from '@platform-ui/auth/hooks/useAuth/index.js';
import { getApplicantSections } from '@platform-ui/utilities/application/config.js';
import { SpecialSectionKeys } from '@platform-ui/utilities/application/configs/standards/types.js';

export default function SectionsOverview(): JSX.Element {
  const { doMutation, loading } = useCreateApplication();
  const config = useApplicationConfig();
  const { user } = useAuth();
  const isAnonymous = user?.core?.isAnonymous ?? false;

  return (
    <FormContainer
      title={isAnonymous ? 'Get ready to apply' : `Welcome, ${user.core.name}!`}
      buttons={
        <Button
          onClick={(): void => {
            doMutation();
          }}
          loading={loading}
        >
          Begin Application
        </Button>
      }
    >
      {isAnonymous ? (
        <div className="lg:w-3/4 mb-6 text-center">
          <Typography variant="largeBody">
            You&apos;re using a guest account. Link your profile to save your application progress
            and manage updates on any device.
          </Typography>
          <div className="mt-4">
            <Button variant="text" link={ApplicantRoutes.PROFILE}>
              Link your account from Profile
            </Button>
          </div>
        </div>
      ) : (
        <div className="lg:w-3/4 mb-6 text-center">
          <Typography variant="largeBody">
            Below, you’ll find an overview of the different sections of the application and what you
            can expect in each section.
          </Typography>
        </div>
      )}
      <Typography variant="h2" color={Color.Text}>
        Application Sections
      </Typography>
      <div className="lg:mt-4 w-full lg:w-7/12">
        <div className="-mx-4 lg:mx-0 flex flex-col gap-y-4">
          {getApplicantSections(config)
            .filter(({ key }) => key !== SpecialSectionKeys.Review)
            .map(({ name: displayName, overview, key }, index) => (
              <Accordion title={displayName} initializeOpen={!index} key={key}>
                <Markdown variant="body">{overview?.description}</Markdown>
              </Accordion>
            ))}
        </div>
      </div>
    </FormContainer>
  );
}
